/**
 * Capability opt-in toggles (Automations cross-object actions, design 2026-06-30 §5.3).
 *
 * Coarse per-capability toggles — Draft replies / Manage calendar / Manage tasks / Manage notes
 * / Manage contacts — that map to the concrete `actionType`s written into an automation's
 * `actionAllowlist`. PRESENTATIONAL: given the current `allowlist`, each toggle reflects whether
 * that capability is on; flipping one calls `onChange` with the NEXT allowlist (computed by
 * `applyCapability`), which the caller persists via the config PATCH (`updateAgentConfig`,
 * validated against the server `ACTION_VOCABULARY` in D7).
 *
 * Every capability DEFAULTS OFF — an empty allowlist renders every toggle unchecked. Uses native
 * checkboxes (role="checkbox"), NOT role="switch", so it never collides with a panel's single
 * enable switch.
 */
import {
  CAPABILITY_GROUPS,
  applyCapability,
  enabledCapabilities,
} from '@/common/utils/action-vocabulary';
import './action-approval-card.css';

export function ActionCapabilityToggles({
  allowlist,
  disabled = false,
  hiddenGroupIds = [],
  onChange,
}: {
  allowlist: readonly string[];
  disabled?: boolean;
  /**
   * Capability group ids to omit from the rendered list. The inbox responder passes `['reply']`
   * — `email.send` is its always-on core function, so a toggle for it would be misleading (and
   * the save path unions `email.send` in regardless of the visible toggles).
   */
  hiddenGroupIds?: readonly string[];
  /** Called with the next allowlist when a capability is toggled — persist it via config PATCH. */
  onChange: (nextAllowlist: string[]) => void;
}) {
  const enabled = enabledCapabilities(allowlist);
  const hidden = new Set(hiddenGroupIds);
  const groups = CAPABILITY_GROUPS.filter((group) => !hidden.has(group.id));

  return (
    <div className="aac-caps" role="group" aria-label="Automation capabilities">
      {groups.map((group) => {
        const on = enabled.has(group.id);
        const inputId = `aac-cap-${group.id}`;
        return (
          <div key={group.id} className="aac-cap">
            <label className="aac-cap__text" htmlFor={inputId}>
              <span className="aac-cap__label">{group.label}</span>
              <span className="aac-cap__desc">{group.description}</span>
            </label>
            <input
              id={inputId}
              type="checkbox"
              className="aac-cap__toggle"
              checked={on}
              disabled={disabled}
              aria-label={group.label}
              onChange={(e) => onChange(applyCapability(allowlist, group, e.target.checked))}
            />
          </div>
        );
      })}
    </div>
  );
}
